import { useState, useEffect } from "react";
import { FaEdit } from "react-icons/fa";
import useProfileStore from "../../../store/builders/useProfileStore";

const Documentation = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const { profile, fetchProfile, updateProfile, isLoading } = useProfileStore();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocName, setSelectedDocName] = useState("");

  const loadProfile = async () => {
    await fetchProfile();
  };

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (profile?.builder) {
      setFormData({
        qualification: profile.builder.qualification || "",
        gst_number: profile.builder.gst_number || "",
        aadhar_number: profile.builder.aadhar_number || "",
        government_license: profile.builder.government_license || null,
        graduation_certificate: profile.builder.graduation_certificate || null
      });
    }
  }, [profile]);

  if (isLoading) {
    return <p>Loading profile...</p>;
  }

  const toggleEdit = () => {
    if (isEditing) {
      setFormData({
        qualification: profile?.builder?.qualification || "",
        gst_number: profile?.builder?.gst_number || "",
        aadhar_number: profile?.builder?.aadhar_number || "",
        government_license: profile?.builder?.government_license || null,
        graduation_certificate: profile?.builder?.graduation_certificate || null
      });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      await updateProfile(formData);
      await loadProfile();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleViewDocument = (docKey) => {
    setSelectedDoc(profile?.builder?.[docKey] || null);
    setSelectedDocName(formatLabel(docKey));
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const { regularFields, documentFields } = {
    regularFields: ['qualification', 'gst_number', 'aadhar_number'],
    documentFields: ['government_license', 'graduation_certificate']
  };

  return (
    <div className="py-2 text-xs">
      <div className="flex items-center justify-between mb-3 border-b border-gray-400 pb-2">
        <h2 className="text-sm font-semibold">Documentation</h2>
        <button 
          className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs"
          onClick={toggleEdit}
        >
          Edit
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {regularFields.map((key) => (
          <div key={key} className="p-1">
            <label className="block text-xs font-medium text-gray-600">
              {formatLabel(key)}
            </label>
            <input
              type="text"
              value={formData[key] || ''}
              onChange={(e) => handleInputChange(key, e.target.value)}
              disabled={!isEditing}
              className={`w-full p-2 mt-1 rounded-md text-xs ${
                isEditing ? "border border-gray-300" : "border-none bg-gray-50"
              }`}
            />
          </div>
        ))}

        {documentFields.map((key) => (
          <div key={key} className="p-1">
            <label className="block text-xs font-medium text-gray-600">
              {formatLabel(key)}
            </label>
            <button
              className="mt-1 px-3 py-1.5 bg-blue-500 text-white rounded-md text-xs hover:bg-blue-600"
              onClick={() => handleViewDocument(key)}
            >
              View
            </button>
          </div>
        ))}
      </div>

      {isEditing && (
        <div className="flex justify-end mt-3 p-2">
          <button 
            className="px-3 py-1.5 bg-gray-500 text-white rounded-md mr-2 hover:bg-gray-600 text-xs"
            onClick={toggleEdit}
          >
            Cancel
          </button>
          <button 
            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs"
            onClick={handleSubmit}
          >
            Save Changes
          </button>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-4 max-w-lg w-full shadow-lg">
            <h3 className="text-sm font-semibold mb-3">{selectedDocName}</h3>
            {selectedDoc ? (
              <iframe
                src={selectedDoc}
                className="w-full h-80"
                title={`${selectedDocName} Preview`}
                sandbox="allow-same-origin allow-scripts"
              />
            ) : (
              <div className="flex items-center justify-center h-80 bg-gray-100 rounded-md">
                <p className="text-xs text-gray-600">No document available</p>
              </div>
            )}
            <button
              className="mt-3 px-3 py-1.5 bg-red-500 text-white rounded-md w-full hover:bg-red-600 text-xs"
              onClick={closeModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const formatLabel = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export default Documentation;